import { useState, useCallback, useEffect, useRef } from "react";
import LiveDashboard from "./LiveDashboard";
import BenchmarkDemo from "./BenchmarkDemo";

function DesktopDataflow({ animating }: { animating: boolean }) {
  return (
    <div className="hidden lg:block relative">
      {/* SVG connection lines with animated dash */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
        {/* Input → Eval Worker */}
        <line x1="14%" y1="50%" x2="26%" y2="50%" stroke="oklch(var(--primary-500) / 0.25)" strokeWidth="2" strokeDasharray="4 4" className={animating ? "dash-animate" : ""} />
        {/* Eval Worker → Benchmarks */}
        <line x1="40%" y1="25%" x2="52%" y2="25%" stroke="oklch(var(--accent-500) / 0.25)" strokeWidth="2" strokeDasharray="4 4" className={animating ? "dash-animate" : ""} />
        {/* Benchmarks → Aggregation */}
        <line x1="66%" y1="25%" x2="78%" y2="25%" stroke="oklch(var(--secondary-500) / 0.25)" strokeWidth="2" strokeDasharray="4 4" className={animating ? "dash-animate" : ""} />
        {/* Eval Worker → Clause Mapping */}
        <line x1="40%" y1="75%" x2="52%" y2="75%" stroke="oklch(var(--primary-500) / 0.25)" strokeWidth="2" strokeDasharray="4 4" className={animating ? "dash-animate" : ""} />
        {/* Clause Mapping → Verdict */}
        <line x1="66%" y1="75%" x2="78%" y2="75%" stroke="oklch(var(--primary-500) / 0.25)" strokeWidth="2" strokeDasharray="4 4" className={animating ? "dash-animate" : ""} />
        {/* Aggregation + Clause Mapping → Audit Report merge */}
        <line x1="90%" y1="25%" x2="90%" y2="75%" stroke="oklch(var(--secondary-500) / 0.25)" strokeWidth="2" className={animating ? "dash-animate" : ""} />
      </svg>

      {/* Traveling dots */}
      {animating && (
        <>
          <div className="dataflow-dot" style={{ animation: "dotTravel1 1.2s ease-in-out forwards", left: "14%", top: "50%" }} />
          <div className="dataflow-dot" style={{ animation: "dotTravel2 1.2s ease-in-out 0.3s forwards", left: "40%", top: "50%" }} />
          <div className="dataflow-dot" style={{ animation: "dotTravel3 1.2s ease-in-out 0.6s forwards", left: "66%", top: "25%" }} />
          <div className="dataflow-dot" style={{ animation: "dotTravel4 1.2s ease-in-out 0.9s forwards", left: "90%", top: "25%" }} />
          <div className="dataflow-dot" style={{ animation: "dotTravel5 1.2s ease-in-out 1.5s forwards", left: "40%", top: "50%" }} />
          <div className="dataflow-dot" style={{ animation: "dotTravel6 1.2s ease-in-out 1.8s forwards", left: "66%", top: "75%" }} />
          <div className="dataflow-dot" style={{ animation: "dotTravel7 1.2s ease-in-out 2.1s forwards", left: "90%", top: "75%" }} />
        </>
      )}

      <div className="relative grid grid-cols-7 gap-4 items-center" style={{ zIndex: 1 }}>
        {/* Input Column */}
        <div className={`col-span-1 flex flex-col gap-3 transition-all duration-700 ${animating ? "opacity-100" : "opacity-90"}`}>
          <div className="bg-foreground-800/50 border border-foreground-700/50 rounded-md p-3 flow-node" style={{ animationDelay: "0s" }}>
            <div className="w-8 h-8 rounded-md bg-primary-500/15 flex items-center justify-center text-primary-400 mb-2">
              <i className="ri-input-method-line text-base" />
            </div>
            <p className="text-xs font-medium text-background-200">Prompts</p>
            <p className="text-[10px] text-foreground-500 mt-0.5">User queries</p>
          </div>
          <div className="bg-foreground-800/50 border border-foreground-700/50 rounded-md p-3 flow-node" style={{ animationDelay: "0.1s" }}>
            <div className="w-8 h-8 rounded-md bg-primary-500/15 flex items-center justify-center text-primary-400 mb-2">
              <i className="ri-server-line text-base" />
            </div>
            <p className="text-xs font-medium text-background-200">Model</p>
            <p className="text-[10px] text-foreground-500 mt-0.5">Cloud or on-prem</p>
          </div>
        </div>

        {/* Arrow */}
        <div className="col-span-1 flex justify-center">
          <i className={`ri-arrow-right-line text-primary-400 text-lg transition-transform duration-300 ${animating ? "translate-x-1" : ""}`} />
        </div>

        {/* Eval Worker */}
        <div className="col-span-1">
          <div className={`bg-primary-500/10 border border-primary-500/25 rounded-md p-3 flow-node transition-all duration-500 ${animating ? "ring-1 ring-primary-400/40" : ""}`} style={{ animationDelay: "0.2s" }}>
            <div className="w-8 h-8 rounded-md bg-primary-500/20 flex items-center justify-center text-primary-400 mb-2">
              <i className="ri-cpu-line text-base" />
            </div>
            <p className="text-xs font-medium text-primary-300">COMPL-AI</p>
            <p className="text-xs font-medium text-primary-300">Eval Worker</p>
            <p className="text-[10px] text-primary-400/60 mt-1">Apache-2.0</p>
          </div>
        </div>

        {/* Branches */}
        <div className="col-span-1 flex flex-col gap-6">
          <div className={`bg-accent-500/10 border border-accent-500/25 rounded-md p-3 flow-node transition-all duration-500 ${animating ? "ring-1 ring-accent-400/40" : ""}`} style={{ animationDelay: "0.4s" }}>
            <div className="w-8 h-8 rounded-md bg-accent-500/20 flex items-center justify-center text-accent-400 mb-2">
              <i className="ri-bar-chart-box-line text-base" />
            </div>
            <p className="text-xs font-medium text-accent-300">30 Benchmarks</p>
            <p className="text-[10px] text-accent-400/60 mt-0.5">6 principles</p>
          </div>
          <div className={`bg-primary-500/10 border border-primary-500/25 rounded-md p-3 flow-node transition-all duration-500 ${animating ? "ring-1 ring-primary-400/40" : ""}`} style={{ animationDelay: "0.5s" }}>
            <div className="w-8 h-8 rounded-md bg-primary-500/20 flex items-center justify-center text-primary-400 mb-2">
              <i className="ri-file-list-3-line text-base" />
            </div>
            <p className="text-xs font-medium text-primary-300">Clause Map</p>
            <p className="text-[10px] text-primary-400/60 mt-0.5">Art. 50 mapping</p>
          </div>
        </div>

        {/* Results */}
        <div className="col-span-1 flex flex-col gap-6">
          <div className={`bg-secondary-500/10 border border-secondary-500/25 rounded-md p-3 flow-node transition-all duration-500 ${animating ? "ring-1 ring-secondary-400/40" : ""}`} style={{ animationDelay: "0.6s" }}>
            <div className="w-8 h-8 rounded-md bg-secondary-500/20 flex items-center justify-center text-secondary-400 mb-2">
              <i className="ri-pie-chart-line text-base" />
            </div>
            <p className="text-xs font-medium text-secondary-300">Aggregate</p>
            <p className="text-[10px] text-secondary-400/60 mt-0.5">Cross-principle</p>
          </div>
          <div className={`bg-primary-500/10 border border-primary-500/25 rounded-md p-3 flow-node transition-all duration-500 ${animating ? "ring-1 ring-primary-400/40" : ""}`} style={{ animationDelay: "0.7s" }}>
            <div className="w-8 h-8 rounded-md bg-primary-500/20 flex items-center justify-center text-primary-400 mb-2">
              <i className="ri-scales-3-line text-base" />
            </div>
            <p className="text-xs font-medium text-primary-300">Verdict</p>
            <p className="text-[10px] text-primary-400/60 mt-0.5">Per clause</p>
          </div>
        </div>

        {/* Merge Arrow */}
        <div className="col-span-1 flex justify-center">
          <i className={`ri-arrow-right-line text-secondary-400 text-lg transition-transform duration-300 ${animating ? "translate-x-1" : ""}`} />
        </div>

        {/* Output */}
        <div className="col-span-1">
          <div className={`bg-secondary-500/10 border border-secondary-500/25 rounded-md p-3 flow-node transition-all duration-500 ${animating ? "ring-1 ring-secondary-400/40" : ""}`} style={{ animationDelay: "0.8s" }}>
            <div className="w-8 h-8 rounded-md bg-secondary-500/20 flex items-center justify-center text-secondary-400 mb-2">
              <i className="ri-shield-check-line text-base" />
            </div>
            <p className="text-xs font-medium text-secondary-300">Audit Report</p>
            <p className="text-[10px] text-secondary-400/60 mt-1">Hash-signed</p>
            <p className="text-[10px] text-secondary-400/60">Replayable</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileDataflow({ animating }: { animating: boolean }) {
  return (
    <div className="lg:hidden flex flex-col gap-2">
      {/* Input */}
      <div className={`bg-foreground-800/50 border border-foreground-700/50 rounded-md p-3 flex items-center gap-3 flow-node transition-all duration-500 ${animating ? "ring-1 ring-primary-400/30" : ""}`} style={{ animationDelay: "0s" }}>
        <div className="w-9 h-9 rounded-md bg-primary-500/15 flex items-center justify-center text-primary-400 shrink-0">
          <i className="ri-input-method-line text-lg" />
        </div>
        <div>
          <p className="text-sm font-medium text-background-200">Prompts + Model Endpoint</p>
          <p className="text-xs text-foreground-500">User queries sent to cloud or self-hosted model</p>
        </div>
      </div>
      <div className="flex justify-center">
        <i className={`ri-arrow-down-line text-primary-400 transition-transform duration-300 ${animating ? "translate-y-1" : ""}`} />
      </div>

      {/* Eval Worker */}
      <div className={`bg-primary-500/10 border border-primary-500/25 rounded-md p-3 flex items-center gap-3 flow-node transition-all duration-500 ${animating ? "ring-1 ring-primary-400/40" : ""}`} style={{ animationDelay: "0.2s" }}>
        <div className="w-9 h-9 rounded-md bg-primary-500/20 flex items-center justify-center text-primary-400 shrink-0">
          <i className="ri-cpu-line text-lg" />
        </div>
        <div>
          <p className="text-sm font-medium text-primary-300">COMPL-AI Eval Worker</p>
          <p className="text-xs text-primary-400/60">Apache-2.0, unmodified</p>
        </div>
      </div>
      <div className="flex justify-center">
        <i className={`ri-arrow-down-line text-accent-400 transition-transform duration-300 ${animating ? "translate-y-1" : ""}`} />
      </div>

      {/* Branches side by side */}
      <div className="grid grid-cols-2 gap-2">
        <div className={`bg-accent-500/10 border border-accent-500/25 rounded-md p-3 flex flex-col items-center text-center flow-node transition-all duration-500 ${animating ? "ring-1 ring-accent-400/40" : ""}`} style={{ animationDelay: "0.4s" }}>
          <div className="w-9 h-9 rounded-md bg-accent-500/20 flex items-center justify-center text-accent-400 mb-2">
            <i className="ri-bar-chart-box-line text-lg" />
          </div>
          <p className="text-xs font-medium text-accent-300">30 Benchmarks</p>
          <p className="text-[10px] text-accent-400/60 mt-0.5">6 principles</p>
        </div>
        <div className={`bg-primary-500/10 border border-primary-500/25 rounded-md p-3 flex flex-col items-center text-center flow-node transition-all duration-500 ${animating ? "ring-1 ring-primary-400/40" : ""}`} style={{ animationDelay: "0.5s" }}>
          <div className="w-9 h-9 rounded-md bg-primary-500/20 flex items-center justify-center text-primary-400 mb-2">
            <i className="ri-file-list-3-line text-lg" />
          </div>
          <p className="text-xs font-medium text-primary-300">Clause Mapping</p>
          <p className="text-[10px] text-primary-400/60 mt-0.5">Art. 50</p>
        </div>
      </div>
      <div className="flex justify-center">
        <i className={`ri-arrow-down-line text-secondary-400 transition-transform duration-300 ${animating ? "translate-y-1" : ""}`} />
      </div>

      {/* Aggregate + Verdict */}
      <div className="grid grid-cols-2 gap-2">
        <div className={`bg-secondary-500/10 border border-secondary-500/25 rounded-md p-3 flex flex-col items-center text-center flow-node transition-all duration-500 ${animating ? "ring-1 ring-secondary-400/40" : ""}`} style={{ animationDelay: "0.6s" }}>
          <div className="w-9 h-9 rounded-md bg-secondary-500/20 flex items-center justify-center text-secondary-400 mb-2">
            <i className="ri-pie-chart-line text-lg" />
          </div>
          <p className="text-xs font-medium text-secondary-300">Aggregate</p>
          <p className="text-[10px] text-secondary-400/60 mt-0.5">Cross-principle</p>
        </div>
        <div className={`bg-primary-500/10 border border-primary-500/25 rounded-md p-3 flex flex-col items-center text-center flow-node transition-all duration-500 ${animating ? "ring-1 ring-primary-400/40" : ""}`} style={{ animationDelay: "0.7s" }}>
          <div className="w-9 h-9 rounded-md bg-primary-500/20 flex items-center justify-center text-primary-400 mb-2">
            <i className="ri-scales-3-line text-lg" />
          </div>
          <p className="text-xs font-medium text-primary-300">Verdict</p>
          <p className="text-[10px] text-primary-400/60 mt-0.5">Per clause</p>
        </div>
      </div>
      <div className="flex justify-center">
        <i className={`ri-arrow-down-line text-secondary-400 transition-transform duration-300 ${animating ? "translate-y-1" : ""}`} />
      </div>

      {/* Output */}
      <div className={`bg-secondary-500/10 border border-secondary-500/25 rounded-md p-3 flex items-center gap-3 flow-node transition-all duration-500 ${animating ? "ring-1 ring-secondary-400/40" : ""}`} style={{ animationDelay: "0.8s" }}>
        <div className="w-9 h-9 rounded-md bg-secondary-500/20 flex items-center justify-center text-secondary-400 shrink-0">
          <i className="ri-shield-check-line text-lg" />
        </div>
        <div>
          <p className="text-sm font-medium text-secondary-300">Hash-Signed Audit Report</p>
          <p className="text-xs text-secondary-400/60">Replayable evidence chain, checksum verified</p>
        </div>
      </div>
    </div>
  );
}

export default function CompliAI() {
  const [animating, setAnimating] = useState(false);
  const [replayKey, setReplayKey] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleReplay = useCallback(() => {
    setAnimating(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    // Small delay to reset, then start
    timerRef.current = setTimeout(() => {
      setReplayKey((k) => k + 1);
      setAnimating(true);
      timerRef.current = setTimeout(() => setAnimating(false), 3500);
    }, 100);
  }, []);

  useEffect(() => {
    // Auto-play once on mount
    const t1 = setTimeout(() => setAnimating(true), 600);
    const t2 = setTimeout(() => setAnimating(false), 3600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <section id="complai" className="py-16 md:py-24 bg-foreground-950 text-background-50">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="max-w-2xl mb-12 md:mb-16">
            <p className="inline-flex items-center gap-2 text-xs md:text-sm font-medium tracking-wide uppercase text-primary-400 mb-3">
              <i className="ri-cpu-line" />
              Under the hood
            </p>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-heading font-semibold text-background-50 leading-tight mb-4">
              Prove it. Don&apos;t just claim it.
            </h2>
            <p className="text-sm md:text-base text-background-300/80 leading-relaxed mb-6">
              Most compliance tools stop at a checklist. We back every model-layer claim with hard benchmark evidence by running the open-source COMPL-AI framework directly against your model endpoints.
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5 text-sm text-background-300/80">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-500/20 text-primary-400 shrink-0 mt-0.5">
                  <i className="ri-check-line text-xs" />
                </span>
                Open-source (Apache-2.0), unmodified — only the article-mapping is ours
              </li>
              <li className="flex items-start gap-2.5 text-sm text-background-300/80">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-500/20 text-primary-400 shrink-0 mt-0.5">
                  <i className="ri-check-line text-xs" />
                </span>
                Pinned versions + raw logs retained for auditor re-runs
              </li>
              <li className="flex items-start gap-2.5 text-sm text-background-300/80">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-500/20 text-primary-400 shrink-0 mt-0.5">
                  <i className="ri-check-line text-xs" />
                </span>
                Works against cloud and self-hosted models alike
              </li>
            </ul>
          </div>

          {/* Dataflow Diagram */}
          <div className="bg-foreground-900 border border-foreground-800 rounded-lg p-5 md:p-8 mb-8">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <span className="text-xs font-medium text-foreground-500">COMPL-AI Dataflow</span>
              <div className="flex items-center gap-3">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-500/20 text-primary-400 font-medium">Live</span>
                <button
                  onClick={handleReplay}
                  className="inline-flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-md bg-primary-500/15 text-primary-400 hover:bg-primary-500/25 transition-colors font-medium whitespace-nowrap cursor-pointer"
                  type="button"
                >
                  <i className="ri-refresh-line" />
                  Replay
                </button>
              </div>
            </div>

            <div key={replayKey}>
              <DesktopDataflow animating={animating} />
              <MobileDataflow animating={animating} />
            </div>

            <p className="text-xs text-foreground-500 mt-6 md:mt-8 text-center">
              Prompts → model → benchmarks → aggregation → clause mapping → verdict → audit report. Replayable at any time.
            </p>
          </div>

          {/* Live Status Dashboard */}
          <LiveDashboard />

          {/* Interactive Benchmark Demo */}
          <BenchmarkDemo />
        </div>
      </div>
    </section>
  );
}